// Cascade-screening candidate selection (pure). Consumes the analyzer's
// per-disease cascade candidates (analysis.family.cascadeCandidates) and applies
// the toolbar's threshold + exclusion filters. It does NOT compute probabilities
// — those come from detectCascadeCandidates, which already excludes affected
// individuals, confirmed/obligate carriers, deceased and dummy nodes. The filters
// here are the ones that aren't already baked in: a probability threshold,
// age-deferred individuals, already-tested individuals, and obligate carriers.
//
// This is the BULK SWEEP: what it returns gets pre-selected for a bulk edit, so
// everything it keeps is being put forward as worth testing. That makes the
// fail-safe direction here the opposite of the detector's — the detector keeps a
// relative in view when in doubt, this filter leaves them out of a bulk action
// when in doubt, and the person is still visible in the panel either way.

import {
  GENOTYPE_RESULT,
  isDefinitiveResult,
  resultForGene
} from './carrierStatusVocabulary';

export const THRESHOLDS = Object.freeze([
  { key: 'p50', labelKey: '≥50%', min: 0.5 },
  { key: 'p25', labelKey: '≥25%', min: 0.25 },
  { key: 'any', labelKey: 'Any non-zero', min: 0 }
]);

/**
 * True if the member has a definitive genotype result for `gene` — from the
 * carrier-status dropdown OR the variant record.
 *
 * The status vocabulary is now the SHARED one (TGCLOUD-1712 W4.4). The private
 * `CONFIRMED_STATUSES` set this replaced omitted `homozygote`, `hemizygote` and
 * `normal`, so a `normal` result counted as untested and the software put a
 * cleared person forward for a test; and it ignored the variant record entirely,
 * so a recorded PATHOGENIC variant with the (default-empty) dropdown unset also
 * read as untested — the John Blythe defect (W4.3).
 *
 * With no `gene` this returns FALSE. Callers use a true result to SUPPRESS the
 * person as already-tested, so the fail-safe direction when we don't know which
 * gene the family is being cascaded for is not to suppress: a result for some
 * unrelated gene says nothing about this family's variant. (TGCLOUD-1712 CT-3 —
 * was `if (!wanted) return true`, so a confirmed result in any gene at all
 * removed the candidate whenever the family had no single implicated gene.)
 *
 * A VUS is NOT a definitive result and does not suppress: it does not resolve
 * carrier state, so the person is still worth testing (W4.3).
 */
export function memberHasGenotypeResult(member, gene) {
  const r = resultForGene(member, gene);
  return !!r && isDefinitiveResult(r.result);
}

/**
 * True if the member has an inconclusive (VUS / self-contradicting) result for
 * `gene`. Exposed so a surface can say WHY a candidate with a result on file is
 * still listed.
 */
export function memberHasInconclusiveResult(member, gene) {
  const r = resultForGene(member, gene);
  return !!r && r.result === GENOTYPE_RESULT.inconclusive;
}

/**
 * Age-deferred per RISK CONTROL RC-PAED-1 (see detectors/cascade.js): a recorded
 * minor OR an unrecorded age. Reads the structured `deferral` first and falls
 * back to the legacy `deferReason` string, so a candidate produced by either
 * shape filters correctly.
 */
export function isAgeDeferred(candidate) {
  if (!candidate) return false;
  if (candidate.deferral) return !!candidate.deferral.deferred;
  return (
    candidate.deferReason === 'minor' || candidate.deferReason === 'age_unknown'
  );
}

/**
 * Candidates the analyzer could not put a number on — an unpriceable inheritance
 * mode, or a result already on file (see cascadePriors MODE HONESTY / W4.3).
 *
 * They fail the threshold filter by construction (there is no probability to
 * compare), so this exists to stop them VANISHING silently: the toolbar can say
 * "N relatives are listed without a prior" instead of quietly dropping people
 * the panel above is showing.
 */
export function unpricedCandidates({
  cascadeCandidates = [],
  diseaseId = null
} = {}) {
  return cascadeCandidates.filter(
    (c) =>
      c &&
      (!diseaseId || c.diseaseId === diseaseId) &&
      c.priorProbability == null
  );
}

// Filter the candidate list for one disease + threshold + exclusions. Returns the
// matching cascade-candidate objects (callers map to pids).
export function selectCascadeCandidates({
  cascadeCandidates = [],
  diseaseId = null,
  minProbability = 0.5,
  excludeMinors = false,
  excludeAlreadyTested = true,
  // Obligate carriers are excluded from the bulk sweep by default (TGCLOUD-1712
  // W4.7). Their genotype is DEDUCED from the pedigree, not predicted, so they
  // do not belong in a probability-thresholded screening selection — the family
  // view already routes them to confirmation instead. This sweep used to include
  // them, which is the third of the three surfaces that disagreed about what an
  // obligate carrier is for.
  excludeObligate = true,
  memberByPid = {}
} = {}) {
  return cascadeCandidates.filter((c) => {
    if (!c) return false;
    if (diseaseId && c.diseaseId !== diseaseId) return false;
    // A null prior means no probability was stated; there is nothing to compare
    // against a threshold, so it cannot enter a threshold-based selection. See
    // unpricedCandidates() — the count must still be surfaced.
    const p = c.priorProbability;
    if (p == null) return false;
    if (!(p > 0 && p >= minProbability)) return false;
    if (excludeObligate && c.obligate) return false;
    if (excludeMinors && isAgeDeferred(c)) return false;
    if (excludeAlreadyTested) {
      const m = memberByPid[String(c.pid)];
      if (m && memberHasGenotypeResult(m, c.variant && c.variant.gene))
        return false;
    }
    return true;
  });
}
